'use client'

/**
 * Organization context — Convex-backed (fresh-start migration).
 * Interface preserved from the legacy organizationsApi/REST version;
 * organizations list is a reactive Convex query (no cache/localStorage
 * plumbing needed). Permission helpers derive from the caller's role on
 * the current org (listMine returns it).
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from 'socialloop-convex/api'
import type { Id } from 'socialloop-convex/dataModel'
import { useAuth } from '@/contexts/auth-context'
import type { OrganizationMember } from '@/lib/organizations-api'

export interface Organization {
  id: string
  name: string
  description?: string
  logoUrl?: string
  logo_url?: string
  website?: string
  email?: string
  phone?: string
  linkedinUrl?: string
  twitterUrl?: string
  instagramUrl?: string
  brandColors?: any
  role?: 'OWNER' | 'ADMIN' | 'MEMBER'
  members: OrganizationMember[]
  [key: string]: any
}

interface OrganizationContextType {
  organizations: Organization[]
  currentOrganization: Organization | null
  loading: boolean
  error: string | null
  loadUserOrganizations: () => Promise<void>
  selectOrganization: (organizationId: string) => Promise<void>
  createOrganization: (data: { name: string; description?: string; website?: string; email?: string; phone?: string }) => Promise<Organization>
  updateOrganization: (id: string, data: any) => Promise<Organization>
  deleteOrganization: (id: string) => Promise<void>
  inviteMember: (organizationId: string, data: any) => Promise<void>
  removeMember: (organizationId: string, memberId: string) => Promise<void>
  updateMemberRole: (organizationId: string, memberId: string, data: any) => Promise<void>
  refreshCurrentOrganization: () => Promise<void>
  hasPermission: (permission: string) => boolean
  hasRole: (roles: string | string[]) => boolean
  isOwner: () => boolean
  isAdmin: () => boolean
  canManageOrganization: () => boolean
  canManageMembers: () => boolean
  canCreateEvents: () => boolean
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const rows = useQuery(api.organizations.listMine, user ? {} : 'skip')
  const createMut = useMutation(api.organizations.create)
  const updateMut = useMutation(api.organizations.update)
  const removeMut = useMutation(api.organizations.remove)
  const inviteMut = useMutation(api.organizations.invite)
  const removeMemberMut = useMutation(api.organizations.removeMember)
  const setRoleMut = useMutation(api.organizations.setMemberRole)

  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem('current_org_id')
  })

  const organizations: Organization[] = useMemo(
    () =>
      (rows ?? []).map((o) => ({
        ...o,
        id: o._id as string,
        logo_url: o.logoUrl,
        members: [] as unknown as OrganizationMember[],
      })),
    [rows]
  )

  const baseCurrent =
    organizations.find((o) => o.id === selectedId) ?? organizations[0] ?? null

  // Members of the current org, mapped to the legacy OrganizationMember shape
  const memberRows = useQuery(
    api.organizations.members,
    baseCurrent ? { organizationId: baseCurrent.id as Id<'organizations'> } : 'skip'
  )
  const currentOrganization: Organization | null = baseCurrent
    ? {
        ...baseCurrent,
        members: (memberRows ?? []).map((m) => ({
          id: m.membershipId as string,
          userId: m.userId as string,
          organizationId: baseCurrent.id,
          role: m.role,
          joinedAt: '',
          user: {
            id: m.userId as string,
            email: m.email,
            fullName: m.fullName,
            full_name: m.fullName,
            avatarUrl: m.avatarUrl,
            avatar_url: m.avatarUrl,
          },
        })) as unknown as OrganizationMember[],
      }
    : null

  const role = currentOrganization?.role

  const value: OrganizationContextType = {
    organizations,
    currentOrganization,
    loading: !!user && rows === undefined,
    error: null,
    loadUserOrganizations: async () => {}, // reactive — no-op
    selectOrganization: async (organizationId: string) => {
      setSelectedId(organizationId)
      try {
        window.localStorage.setItem('current_org_id', organizationId)
      } catch {}
    },
    createOrganization: async (data) => {
      const id = await createMut({
        name: data.name,
        description: data.description,
        website: data.website,
        email: data.email,
      })
      setSelectedId(id as string)
      return { ...data, id: id as string, members: [] } as unknown as Organization
    },
    updateOrganization: async (id, data) => {
      await updateMut({
        organizationId: id as Id<'organizations'>,
        patch: {
          name: data.name,
          description: data.description,
          logoUrl: data.logoUrl ?? data.logo_url,
          website: data.website,
          email: data.email,
          phone: data.phone,
          linkedinUrl: data.linkedinUrl ?? data.linkedin_url,
          twitterUrl: data.twitterUrl ?? data.twitter_url,
          instagramUrl: data.instagramUrl ?? data.instagram_url,
          brandColors: data.brandColors ?? data.brand_colors,
        },
      })
      return { ...data, id, members: [] } as unknown as Organization
    },
    deleteOrganization: async (id) => {
      await removeMut({ organizationId: id as Id<'organizations'> })
      if (selectedId === id) setSelectedId(null)
    },
    inviteMember: async (organizationId, data) => {
      await inviteMut({
        organizationId: organizationId as Id<'organizations'>,
        email: data.email,
        role: (data.role ?? 'MEMBER').toUpperCase() === 'ADMIN' ? 'ADMIN' : 'MEMBER',
      })
    },
    removeMember: async (_organizationId, memberId) => {
      await removeMemberMut({ membershipId: memberId as Id<'orgMembers'> })
    },
    updateMemberRole: async (_organizationId, memberId, data) => {
      await setRoleMut({
        membershipId: memberId as Id<'orgMembers'>,
        role: (data.role ?? 'MEMBER').toUpperCase() === 'ADMIN' ? 'ADMIN' : 'MEMBER',
      })
    },
    refreshCurrentOrganization: async () => {}, // reactive — no-op
    hasPermission: () => role === 'OWNER' || role === 'ADMIN',
    hasRole: (roles) => {
      const list = Array.isArray(roles) ? roles : [roles]
      return !!role && list.map((r) => r.toUpperCase()).includes(role)
    },
    isOwner: () => role === 'OWNER',
    isAdmin: () => role === 'OWNER' || role === 'ADMIN',
    canManageOrganization: () => role === 'OWNER' || role === 'ADMIN',
    canManageMembers: () => role === 'OWNER' || role === 'ADMIN',
    canCreateEvents: () => !!role,
  }

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}
